import { useCallback, useEffect, useState } from 'react';
import {
  catalogopsApi,
  type BulkJobRecord,
  type CatalogOpsDashboard,
  type CatalogRecommendation,
  type ComplianceIssue,
  type ListingHealthRecord,
  type ListingRecord,
  type VariationRecord,
} from '../../api/catalogops';

type Tab = 'explorer' | 'listings' | 'variations' | 'bulk' | 'health' | 'compliance' | 'recommendations';

function statusClass(s: string) {
  if (s === 'active') return 'text-emerald-700 bg-emerald-50';
  if (s === 'suppressed') return 'text-red-700 bg-red-50';
  if (s === 'draft') return 'text-amber-700 bg-amber-50';
  if (s === 'inactive') return 'text-slate-600 bg-slate-50';
  return 'text-slate-600 bg-slate-50';
}

function healthClass(score: number) {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 60) return 'text-amber-700';
  return 'text-red-700';
}

function severityClass(s?: string) {
  if (s === 'critical') return 'text-red-700 bg-red-50';
  if (s === 'high') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

export function CatalogOpsPage() {
  const [tab, setTab] = useState<Tab>('explorer');
  const [dashboard, setDashboard] = useState<CatalogOpsDashboard | null>(null);
  const [listings, setListings] = useState<ListingRecord[]>([]);
  const [variations, setVariations] = useState<VariationRecord[]>([]);
  const [health, setHealth] = useState<ListingHealthRecord[]>([]);
  const [bulkJobs, setBulkJobs] = useState<BulkJobRecord[]>([]);
  const [recommendations, setRecommendations] = useState<CatalogRecommendation[]>([]);
  const [compliance, setCompliance] = useState<ComplianceIssue[]>([]);
  const [selectedListing, setSelectedListing] = useState<ListingRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, lst, vars, hlth, bulk, recs, comp] = await Promise.all([
        catalogopsApi.dashboard(),
        catalogopsApi.listListings(),
        catalogopsApi.listVariations(),
        catalogopsApi.listHealth(),
        catalogopsApi.listBulkJobs(),
        catalogopsApi.listRecommendations(),
        catalogopsApi.listCompliance(),
      ]);
      setDashboard(dash);
      setListings(lst);
      setVariations(vars);
      setHealth(hlth);
      setBulkJobs(bulk);
      setRecommendations(recs);
      setCompliance(comp);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load catalog operations data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try {
      await catalogopsApi.generateRecommendations();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'explorer', label: 'Catalog Explorer' },
    { id: 'listings', label: 'Listing Workspace' },
    { id: 'variations', label: 'Variation Manager' },
    { id: 'bulk', label: 'Bulk Operations' },
    { id: 'health', label: 'Listing Health' },
    { id: 'compliance', label: 'Compliance Console' },
    { id: 'recommendations', label: 'Recommendation Workspace' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Catalog &amp; Listing Operations</h1>
          <p className="text-sm text-slate-500">
            Catalog management, listing health, variations, bulk ops &amp; AI copilot — APP-00001
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'explorer' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Total Listings', value: dashboard.total_listings.toString() },
              { label: 'Active', value: dashboard.active_listings.toString() },
              { label: 'Suppressed', value: dashboard.suppressed_listings.toString() },
              { label: 'Drafts', value: dashboard.draft_listings.toString() },
              { label: 'Variation Families', value: dashboard.variation_families.toString() },
              { label: 'Avg Health Score', value: dashboard.avg_health_score.toFixed(0) },
              { label: 'Compliance Issues', value: dashboard.compliance_issues.toString() },
              { label: 'Active Bulk Jobs', value: dashboard.active_bulk_jobs.toString() },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2">SKU</th>
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Health</th>
                  <th className="px-4 py-2">Attributes</th>
                  <th className="px-4 py-2">Marketplace</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-mono text-xs">{l.sku}</td>
                    <td className="px-4 py-2 max-w-xs truncate">{l.title}</td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(l.status)}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className={`px-4 py-2 font-medium ${healthClass(l.health_score)}`}>{l.health_score.toFixed(0)}</td>
                    <td className="px-4 py-2">{l.attribute_completeness_pct.toFixed(0)}%</td>
                    <td className="px-4 py-2 text-slate-500">{l.marketplace}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'listings' && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
            {listings.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => setSelectedListing(l)}
                className={`w-full rounded-lg border p-3 text-left text-sm ${
                  selectedListing?.id === l.id ? 'border-sky-300 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}
              >
                <p className="font-medium text-slate-900">{l.sku}</p>
                <p className="truncate text-xs text-slate-500">{l.title}</p>
              </button>
            ))}
          </div>
          {selectedListing && (
            <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-medium text-slate-900">{selectedListing.title}</h2>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(selectedListing.status)}`}>
                  {selectedListing.status}
                </span>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
                <div><span className="text-slate-500">SKU:</span> {selectedListing.sku}</div>
                <div><span className="text-slate-500">ASIN:</span> {selectedListing.asin ?? '—'}</div>
                <div><span className="text-slate-500">Brand:</span> {selectedListing.brand ?? '—'}</div>
                <div><span className="text-slate-500">Category:</span> {selectedListing.category ?? '—'}</div>
                <div><span className="text-slate-500">Health:</span> <span className={healthClass(selectedListing.health_score)}>{selectedListing.health_score.toFixed(0)}</span></div>
                <div><span className="text-slate-500">Attributes:</span> {selectedListing.attribute_completeness_pct.toFixed(0)}%</div>
                <div><span className="text-slate-500">Version:</span> v{selectedListing.version}</div>
                {selectedListing.is_parent && <div><span className="text-slate-500">Type:</span> Parent</div>}
                {selectedListing.parent_sku && <div><span className="text-slate-500">Parent:</span> {selectedListing.parent_sku}</div>}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'variations' && (
        <div className="space-y-4">
          {variations.map((v) => (
            <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{v.parent_sku}</h3>
                  <p className="text-sm text-slate-500">Theme: {v.theme ?? '—'} · {v.child_count} children</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${healthClass(v.health_score)}`}>{v.health_score.toFixed(0)}</span>
                  {!v.is_valid && (
                    <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">invalid</span>
                  )}
                </div>
              </div>
              {v.child_skus && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {v.child_skus.map((s) => (
                    <span key={s} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{s}</span>
                  ))}
                </div>
              )}
              {v.issues && v.issues.length > 0 && (
                <ul className="mt-2 list-inside list-disc text-xs text-red-600">
                  {v.issues.map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'bulk' && (
        <div className="space-y-3">
          {bulkJobs.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-medium text-slate-900">{b.operation.replace(/_/g, ' ')}</h3>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(b.status === 'completed' ? 'active' : b.status === 'running' ? 'draft' : 'inactive')}`}>
                  {b.status}
                </span>
              </div>
              <div className="mt-2 flex gap-4 text-sm text-slate-600">
                <span>{b.processed_items}/{b.total_items} processed</span>
                {b.failed_items > 0 && <span className="text-red-600">{b.failed_items} failed</span>}
              </div>
              {b.status === 'running' && (
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-sky-500"
                    style={{ width: `${(b.processed_items / b.total_items) * 100}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'health' && (
        <div className="space-y-3">
          {health.map((h) => (
            <div key={h.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-medium">{h.sku}</span>
                <span className={`font-medium ${healthClass(h.health_score)}`}>{h.health_score.toFixed(0)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(h.severity)}`}>
                  {h.severity}
                </span>
              </div>
              {h.issue && <p className="mt-1 text-sm text-slate-600">{h.issue}</p>}
              {h.issue_type && <p className="mt-1 text-xs text-slate-400">{h.issue_type.replace(/_/g, ' ')}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'compliance' && (
        <div className="space-y-3">
          {compliance.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{c.sku}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(c.severity)}`}>
                  {c.severity}
                </span>
                <span className="text-xs text-slate-400">{c.rule.replace(/_/g, ' ')}</span>
              </div>
              {c.description && <p className="mt-1 text-sm text-slate-600">{c.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={generateRecs}
              disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                  #{r.priority_rank}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                {r.sku && <span>SKU: {r.sku}</span>}
                <span>{r.type.replace(/_/g, ' ')}</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.expected_impact && <span>Impact: {r.expected_impact}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
